import { useFormContext, useWatch } from 'react-hook-form'

import type { SalesReturnFormValues } from '@/lib/schemas/sales-return-schemas'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import { Textarea } from '@/components/ui/textarea'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /**
     * Phí tối đa theo xem trước (`fee_max`): tiền khách đã trả dư sau khi trừ
     * giá trị hàng trả. Cha chỉ hiện section khi số này > 0.
     */
    feeMax: number
}

/**
 * Phí trả hàng (M-44): phần tiền khách ĐÃ TRẢ mà bên bán giữ lại, không hoàn.
 * Chỉ có khi đơn đã thu tiền; "Không trả lại tiền" = giữ lại toàn bộ phần có
 * thể hoàn.
 */
export function ReturnFeeSection({ feeMax }: Props) {
    const { control, register, setValue, clearErrors } =
        useFormContext<SalesReturnFormValues>()
    const fee = useWatch({ control, name: 'fee_amount' })
    const hasFee = Number(fee) > 0
    const keepAll = Number(fee) === feeMax

    const setFee = (value: string) => {
        setValue('fee_amount', value, { shouldDirty: true })
        clearErrors('fee_amount')
    }

    return (
        <section className="space-y-3 rounded-lg border p-3 sm:p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold">
                        {m.sales_return_fee_title()}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        {m.sales_return_fee_hint({ max: formatVnd(feeMax) })}
                    </p>
                </div>
                <Button
                    type="button"
                    variant={keepAll ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-11 sm:h-9"
                    aria-pressed={keepAll}
                    onClick={() => setFee(keepAll ? '' : String(feeMax))}
                >
                    {m.sales_return_fee_keep_all()}
                </Button>
            </div>
            <div className="space-y-1">
                <Label htmlFor="return-fee-amount">
                    {m.sales_return_fee_label()}
                </Label>
                <Input
                    id="return-fee-amount"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    placeholder="0"
                    className="h-11 text-base sm:w-60"
                    aria-describedby="return-fee-amount-preview"
                    {...register('fee_amount')}
                />
                <MoneyPreview id="return-fee-amount-preview" value={fee} />
                <FieldError name="fee_amount" />
            </div>
            {hasFee && (
                <div className="space-y-1">
                    <Label htmlFor="return-fee-reason" required>
                        {m.sales_return_fee_reason_label()}
                    </Label>
                    <Textarea
                        id="return-fee-reason"
                        rows={2}
                        className="text-base sm:text-sm"
                        placeholder={m.sales_return_fee_reason_placeholder()}
                        {...register('fee_reason')}
                    />
                    <FieldError name="fee_reason" />
                    <p className="text-sm text-muted-foreground">
                        {m.sales_return_fee_effect({
                            refund: formatVnd(
                                Math.max(0, feeMax - Number(fee)),
                            ),
                        })}
                    </p>
                </div>
            )}
        </section>
    )
}
